import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Grid,
  Stack,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";


import { Save } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { getLandmark, updateLandmark } from "../../api/landmark";
import { listStations } from "../../api/station";
import { Station, Landmark, LandmarkActivityDTO } from "../../types/api";
import ImageUploadTab, { ImageItem } from "../../components/landmark/ImageUploadTab";
import { TabPanel } from "../../components/TabPanel";
import { uploadMultiFile } from "../../api/file";

import { ActivityItem, ActivitySchedule, ActivityTabsEditor } from "../../components/landmark/ActivityTab";
import LandmarkViewAnalytics from "../../components/landmark/landmarkAnalytic";

// ✅ import component tab รูปภาพ (ของเมื่อกี้)


delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function FlyToLocation({ position }: { position: [number, number] }) {
  const map = useMap();
  map.flyTo(position, map.getZoom());
  return null;
}


export default function LandmarkEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);

  const [tab, setTab] = useState(0);

  const [nameTh, setNameTh] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [nameZh, setNameZh] = useState("");
  const [latInput, setLatInput] = useState("0");
  const [lngInput, setLngInput] = useState("0");
  const [description, setDescription] = useState("")
  const [markerPos, setMarkerPos] = useState<[number, number]>([0, 0]);
  const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);

  const [stations, setStations] = useState<Station[]>([]);
  const [stationId, setStationId] = useState<number | "">("");

  const [imageItems, setImageItems] = useState<ImageItem[]>([]);

  const [activities, setActivities] = useState<ActivityItem[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [landmarkData, stationData] = await Promise.all([
          getLandmark(Number(id)),
          listStations(),
        ]);

        setStations(stationData);

        setNameTh(landmarkData.nameTh || "");
        setNameEn(landmarkData.nameEn || "");
        setNameZh(landmarkData.nameZh || "");
        setLatInput(landmarkData.latitude.toString());
        setLngInput(landmarkData.longitude.toString());
        setMarkerPos([landmarkData.latitude, landmarkData.longitude]);
        setStationId(landmarkData.stationId);
        setDescription(landmarkData.description || "");

        // 🖼 รูปภาพ (ของเดิมคุณทำถูกแล้ว)
        setImageItems(
          (landmarkData.landmarkImages ?? [])
            .slice()
            .sort((a, b) => (a.orderSeq ?? 0) - (b.orderSeq ?? 0))
            .map((img, idx) => ({
              id: `db-${img.id ?? idx}`,
              imagePath: img.imagePath ?? "",
              title: img.title ?? "",
              orderSeq: img.orderSeq ?? idx,
              file: undefined,
            }))
        );


        setActivities(
          (landmarkData.landmarkActivities ?? []).map(dtoToUiActivity)
        );

      } catch (err: any) {
        alert("โหลดข้อมูลล้มเหลว: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);


  // const updateMarker = () => {
  //   const lat = parseFloat(latInput);
  //   const lng = parseFloat(lngInput);
  //   if (!isNaN(lat) && !isNaN(lng)) {
  //     const pos: [number, number] = [lat, lng];
  //     setMarkerPos(pos);
  //     setFlyTarget(pos);
  //   } else {
  //     alert("กรุณากรอก Latitude และ Longitude ให้ถูกต้อง");
  //   }
  // };

  const handleSubmit = async () => {
    try {
      if (!stationId) {
        alert("กรุณาเลือกสถานีใกล้เคียง");
        return;
      }

      // ===== Landmark Images (ของเดิมคุณโอเค) =====
      const newImageItems = imageItems.filter((x) => x.file);

      const uploaded = newImageItems.length
        ? await uploadMultiFile(newImageItems.map((x) => x.file!))
        : [];

      const merged = [...imageItems];
      let uploadIndex = 0;

      merged.forEach((item, idx) => {
        if (item.file) {
          merged[idx] = {
            ...item,
            imagePath: uploaded[uploadIndex].imagePath,
            file: undefined,
          };
          uploadIndex++;
        }
      });

      // ===== Activity Images (ใช้ safe) =====
      const activitiesWithUploadedImages = await uploadActivityImagesSafe(
        activities,
        uploadMultiFile
      );

      // ✅ อัปเดต state ให้ UI เห็นว่ารูปกลายเป็น url แล้ว
      setActivities(activitiesWithUploadedImages);

      const updatedLandmark: Partial<Landmark> = {
        nameTh,
        nameEn,
        nameZh,
        description,
        latitude: parseFloat(latInput),
        longitude: parseFloat(lngInput),
        stationId,

        landmarkImages: merged
          .filter((x) => x.imagePath) // ✅ กันรูปว่าง
          .map((x, idx) => ({
            imagePath: x.imagePath,
            title: x.title?.trim() || null,
            orderSeq: idx,
          })),

        // ✅ ส่งกิจกรรม (พร้อม description + images)
        landmarkActivities: activitiesWithUploadedImages.map(uiToDtoActivity),
      };

      await updateLandmark(Number(id), updatedLandmark);

      alert("แก้ไข Landmark เรียบร้อย");
    } catch (err: any) {
      alert("แก้ไขล้มเหลว: " + err.message);
    }
  };





  function MapClickHandler({ setMarker }: { setMarker: (pos: [number, number]) => void }) {
    useMapEvents({
      click(e) {
        const pos: [number, number] = [e.latlng.lat, e.latlng.lng];
        setMarker(pos);
      },
    });
    return null;
  }

  if (loading) return <Typography>กำลังโหลด...</Typography>;

  return (
    <Box>
      <Typography variant="h6" mb={2}>
        แก้ไข Landmark
      </Typography>

      {/* ✅ Tabs header */}

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="ข้อมูลสถานที่" />
        <Tab label={`รูปภาพ (${imageItems.length})`} />
        <Tab label="นิทรรศการ / กิจกรรม " />
        <Tab label="สถิติผู้เข้าชม" />
      </Tabs>


      <Paper sx={{ p: 3, mt: 2 }}>
        {/* TAB 1: ของเดิม */}
        <TabPanel value={tab} index={0}>
          <Stack gap={3}>
            <Grid container spacing={2}>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label="ชื่อ (ไทย)"
                  value={nameTh}
                  onChange={(e) => setNameTh(e.target.value)}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label="ชื่อ (อังกฤษ)"
                  value={nameEn}
                  onChange={(e) => setNameEn(e.target.value)}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  fullWidth
                  label="ชื่อ (จีน)"
                  value={nameZh}
                  onChange={(e) => setNameZh(e.target.value)}
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  select
                  fullWidth
                  label="สถานีใกล้เคียง"
                  value={stationId}
                  onChange={(e) => setStationId(Number(e.target.value))}
                >
                  {stations.map((st) => (
                    <MenuItem key={st.id} value={st.id}>
                      {st.nameTh} ({st.nameEn})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  value={latInput}
                  onChange={(e) => setLatInput(e.target.value)}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  value={lngInput}
                  onChange={(e) => setLngInput(e.target.value)}
                />
              </Grid>
              <Grid size={11}>

                <TextField
                  fullWidth
                  label="รายละเอียดสถานที่ (Description)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  minRows={4}
                  maxRows={8}
                  placeholder="อธิบายรายละเอียดของสถานที่ ประวัติ ความน่าสนใจ ฯลฯ"
                />
              </Grid>
            </Grid>

            <Box sx={{ height: 400 }}>
              <MapContainer center={markerPos} zoom={16} style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={markerPos} />
                {flyTarget && <FlyToLocation position={flyTarget} />}
                <MapClickHandler
                  setMarker={(pos) => {
                    setMarkerPos(pos);
                    setLatInput(pos[0].toString());
                    setLngInput(pos[1].toString());
                  }}
                />
              </MapContainer>
            </Box>

            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="contained" color="primary" startIcon={<Save />} onClick={handleSubmit}>
                บันทึก
              </Button>
            </Box>
          </Stack>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <ImageUploadTab
            maxFiles={10}
            value={imageItems}
            onChange={setImageItems}
          />

          {/* ปุ่มบันทึกด้านล่างจะให้เหมือนกันก็ได้ */}
          <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 3 }}>
            <Button variant="contained" color="primary" startIcon={<Save />} onClick={handleSubmit}>
              บันทึก
            </Button>
          </Box>
        </TabPanel>
        <TabPanel value={tab} index={2}>
          <ActivityTabsEditor value={activities} onChange={setActivities} />

          <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 3 }}>
            <Button variant="contained" color="primary" startIcon={<Save />} onClick={handleSubmit}>
              บันทึก
            </Button>
          </Box>
        </TabPanel>
        <TabPanel value={tab} index={3}>
          <LandmarkViewAnalytics landmarkId={Number(id)} />
        </TabPanel>
      </Paper>
    </Box>
  );
}

const daysToCsv = (days: number[]) =>
  Array.from(new Set(days))
    .map(Number)
    .filter((n) => n >= 1 && n <= 7)
    .sort((a, b) => a - b)
    .join(",");

const csvToDays = (csv?: string | null) =>
  (csv ?? "")
    .split(",")
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n) && n >= 1 && n <= 7);

const stripDb = (id?: string) => (id?.startsWith("db-") ? id.slice(3) : id);

const uiToDtoActivity = (it: ActivityItem): LandmarkActivityDTO => {
  const s = it.schedule;

  const base: Omit<LandmarkActivityDTO, "mode"> = {
    id: stripDb(it.id),
    title: it.title?.trim() || null,
    description: it.descriptionHtml ?? null,
    isActive: !!it.isActive,

    images: (it.images ?? [])
      .filter((img) => !!img.imagePath)
      .slice()
      .sort((a, b) => (a.orderSeq ?? 0) - (b.orderSeq ?? 0))
      .map((img, idx) => ({
        id: stripDb(img.id),
        title: img.title?.trim() || null,
        imagePath: img.imagePath!,
        orderSeq: idx,
      })),

    // fields schedule บางส่วนจะใส่ในแต่ละ branch
    startDate: null,
    endDate: null,
    repeatType: null,
    repeatStart: null,
    weeklyDays: null,
    dayOfMonth: null,
  };

  if (s.mode === "RANGE") {
    return {
      ...base,
      mode: "RANGE",
      startDate: s.startDate || null,
      endDate: s.endDate || null,
    };
  }

  if (s.repeat.type === "WEEKLY") {
    return {
      ...base,
      mode: "REPEAT",
      repeatType: "WEEKLY",
      repeatStart: s.startDate || null,
      weeklyDays: daysToCsv(s.repeat.daysOfWeek ?? [1]),
    };
  }

  return {
    ...base,
    mode: "REPEAT",
    repeatType: "MONTHLY",
    repeatStart: s.startDate || null,
    dayOfMonth: s.repeat.dayOfMonth ?? 1,
  };
};

const toYMD = (v: any) => {
  if (!v) return "";
  if (v instanceof Date) return v.toISOString().slice(0, 10);

  if (typeof v === "string") return v.slice(0, 10);
  try {
    return new Date(v).toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const dtoToUiActivity = (dto: any): ActivityItem => {
  const id = dto.id ? `db-${dto.id}` : `tmp-${Date.now()}-${Math.random()}`;

  let schedule: ActivitySchedule;

  if (dto.mode === "RANGE") {
    schedule = {
      mode: "RANGE",
      startDate: toYMD(dto.startDate),
      endDate: toYMD(dto.endDate),
    };
  } else {
    if (dto.repeatType === "MONTHLY") {
      schedule = {
        mode: "REPEAT",
        startDate: toYMD(dto.repeatStart),
        repeat: { type: "MONTHLY", dayOfMonth: dto.dayOfMonth ?? 1 },
      };
    } else {
      schedule = {
        mode: "REPEAT",
        startDate: toYMD(dto.repeatStart),
        repeat: {
          type: "WEEKLY",
          daysOfWeek: (csvToDays(dto.weeklyDays) as any) ?? [1],
        },
      };
    }
  }

  return {
    id,
    title: dto.title ?? "",
    descriptionHtml: dto.description ?? "",
    isActive: !!dto.isActive,
    schedule,
    images: (dto.images ?? [])
      .slice()
      .sort((a: any, b: any) => (a.orderSeq ?? 0) - (b.orderSeq ?? 0))
      .map((img: any, idx: number) => ({
        id: img.id ? `db-${img.id}` : `tmp-img-${id}-${idx}`,
        title: img.title ?? "",
        imagePath: img.imagePath,
        orderSeq: img.orderSeq ?? idx,
        file: undefined,
        previewUrl: undefined,
      })),
  };
};




type UploadBind = { actId: string; imgId: string };

async function uploadActivityImagesSafe(
  activities: ActivityItem[],
  uploadMultiFileFn: (files: File[]) => Promise<{ imagePath: string }[]>
) {
  // 1) รวบรวมไฟล์ทั้งหมดตามลำดับ "วน activity -> วน images"
  const files: File[] = [];
  activities.forEach((act) => {
    (act.images ?? []).forEach((img) => {
      if (img.file) files.push(img.file);
    });
  });

  if (!files.length) return activities;

  // 2) upload ทีเดียว
  const uploaded = await uploadMultiFileFn(files);

  // 3) ไล่ใส่กลับตามลำดับเดิม 100%
  let upIdx = 0;
  const next = activities.map((act) => {
    const newImages = (act.images ?? []).map((img) => {
      if (!img.file) return img;

      const url = uploaded[upIdx]?.imagePath;
      upIdx++;

      return {
        ...img,
        imagePath: url,
        file: undefined,
        previewUrl: undefined,
      };
    });

    return {
      ...act,
      images: newImages.map((x, i) => ({ ...x, orderSeq: i })),
    };
  });

  return next;
}
